import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { DataToApprove, ISaveApprovalStatusDto, Group, ApprovalDetails, Dictionary } from '../../../models';
import { IGroupApprovalService } from '../../../services';
import { MomentHelper, momentFormat } from '../../../common';

interface IsGroupApproved {
    [guid: string]: {
        isApproved: boolean;
        label: string;
    };
}

interface SaveApprovedDataResult {
    dtoList: ISaveApprovalStatusDto[];
    messages: string[];
}

export interface IApprovalManagementService {
    saveDataToApprove(dataToApproveList: DataToApprove[], groupsDictionary: { [guid: string]: Group }): angular.IPromise<SaveApprovedDataResult>;
    /**
     * Retrieves the approval status of a list of groups.
     * @param selectedDate The date at which results must be retrieved.
     * @param guidList The list of group guids to retrieve.
     */
    getGroupApprovalStatuses(dates: Dictionary<Date[]>, guidList: string[]): angular.IPromise<IsGroupApproved>;
}

export class ApprovalManagementService implements IApprovalManagementService {
    static serviceId = 'approvalManagementService';
    static $inject = ['$q', '$translate', 'groupApprovalService'];

    constructor(
        private _$q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private _groupApprovalService: IGroupApprovalService
    ) {}

    /**
     * Update approve with justification if needed
     * @param groupGuid Group guid
     * @param date Date to approve
     * @param level Level to approve
     * @param status Status
     * @param comment Comment
     */
    saveDataToApprove(dataToApproveList: DataToApprove[], groupsDictionary: { [guid: string]: Group }): angular.IPromise<SaveApprovedDataResult> {
        const promiseList: angular.IPromise<string>[] = [];
        const dtoList: ISaveApprovalStatusDto[] = [];
        dataToApproveList.forEach((dataToApprove) => {
            const group = groupsDictionary[dataToApprove.groupGuid];
            const frequency = group.frequency;
            const endDateToApprove = dataToApprove.endDateToApprove
                ? dataToApprove.endDateToApprove.toISOString()
                : frequency.interval
                ? moment(dataToApprove.dateToApprove).add(frequency.interval, MomentHelper.getPrefix(frequency.intervalTimeUnit)).toISOString()
                : moment(dataToApprove.dateToApprove).add('day', 1).toISOString();
            const dto: ISaveApprovalStatusDto = {
                groupGuid: dataToApprove.groupGuid,
                level: dataToApprove.level,
                beginDate: dataToApprove.dateToApprove.toISOString(),
                endDate: endDateToApprove,
                status: dataToApprove.status,
                comment: dataToApprove.comment
            };
            //TODO could we build the list and make a single call?
            promiseList.push(this._groupApprovalService.saveStatuses([dto]));
            dtoList.push(dto);
        });
        return this._$q.all(promiseList).then((responseList) => {
            return { dtoList, messages: responseList.filter((d) => !!d) };
        });
    }

    getGroupApprovalStatuses(dates: Dictionary<Date[]>, guidList: string[]) {
        const isGroupApproved: IsGroupApproved = {};
        const dateTimeList = Object.values(dates).flatMap((dateList) => dateList);
        return this._groupApprovalService.getApprovalDetails({ guidList: guidList, dateTimeList }).then((approvalDetails) => {
            // Approval details
            // let approvalDetails = responses[0];
            if (dateTimeList?.length > 0) {
                const reference = dateTimeList[0].getTime();
                guidList.forEach((guid) => {
                    const groupDates = dates[guid];
                    // Define locked state
                    const details = approvalDetails[guid];
                    const detailsIsEmpty = !details || details.length === 0;
                    const allDatesAreApproved =
                        !detailsIsEmpty &&
                        _.every(groupDates, (date) => {
                            return !!_.find(details, (d: ApprovalDetails) => new Date(d.timestamp).getTime() === date.getTime());
                        });
                    const found = _.find(details, (d: ApprovalDetails) => new Date(d.timestamp).getTime() === reference);
                    let label = '';
                    if (!!found && allDatesAreApproved) {
                        const username = found.user;
                        label = this._$translate.instant('Locked because group _group_ has been approved by _user_ on _date_.', {
                            group: found.group.uniqueName,
                            user: username,
                            date: moment(found.approvalDate).format(momentFormat)
                        });
                    }
                    isGroupApproved[guid] = {
                        isApproved: allDatesAreApproved,
                        label
                    };
                });
            } else {
                guidList.forEach((guid) => {
                    isGroupApproved[guid] = {
                        isApproved: false,
                        label: undefined
                    };
                });
            }
            return isGroupApproved;
        });
    }
}
